import React, { useState, useRef } from "react";
import {
  Box,
  TextField,
  IconButton,
  InputAdornment,
  useTheme,
  Paper,
  Tooltip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { useTranslation } from "react-i18next";

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({ onSendMessage, disabled = false }) => {
  const [message, setMessage] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const { t } = useTranslation();

  const handleSend = () => {
    if (message.trim() && !disabled) {
      onSendMessage(message);
      setMessage("");
      // Maintain focus on input after sending
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey && !disabled) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  // Function to focus the input field programmatically
  const focusInput = () => {
    if (!disabled) {
      inputRef.current?.focus();
    }
  };

  // Visual feedback for send button
  const getSendButtonStyles = () => {
    if (disabled) {
      return {
        backgroundColor: 'transparent',
        color: theme.palette.text.disabled,
        cursor: 'not-allowed',
      };
    }

    if (!message.trim()) {
      return {
        backgroundColor: 'transparent',
        color: theme.palette.text.disabled,
      };
    }

    return {
      backgroundColor: theme.palette.mode === 'dark' ? '#10a37f' : '#19c37d',
      color: '#ffffff',
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark' ? '#0e8c6a' : '#0ea67a',
      },
    };
  };

  return (
    <Box 
      sx={{ 
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        p: 2,
        pb: 4,
        backgroundColor: theme.palette.background.default,
        borderTop: `1px solid ${theme.palette.divider}`,
        zIndex: 10,
      }}
    >
      <Box 
        sx={{ 
          maxWidth: "48rem",
          mx: "auto",
          position: "relative",
        }}
      >
        <Paper
          elevation={3}
          onClick={focusInput}
          sx={{
            borderRadius: "0.75rem",
            border: `1px solid ${isFocused && !disabled
              ? theme.palette.mode === 'dark' 
                ? '#10a37f' 
                : '#19c37d'
              : theme.palette.divider}`,
            boxShadow: isFocused && !disabled
              ? theme.palette.mode === 'dark' 
                ? '0px 0px 10px rgba(16, 163, 127, 0.3)' 
                : '0px 0px 15px rgba(25, 195, 125, 0.2)'
              : theme.palette.mode === 'dark' 
                ? '0px 0px 10px rgba(0, 0, 0, 0.2)' 
                : '0px 0px 15px rgba(0, 0, 0, 0.05)',
            transition: 'all 0.2s ease',
            cursor: disabled ? 'default' : 'text',
            opacity: disabled ? 0.7 : 1,
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={8}
            minRows={2}
            value={message}
            disabled={disabled}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            onFocus={handleFocus}
            onBlur={handleBlur}
            inputRef={inputRef}
            placeholder={disabled ? t("Waiting for response...") : t("Message Shoeb's portfolio assistant...")}
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={disabled ? t("Waiting for response") : ""}>
                    <span>
                      <IconButton
                        onClick={handleSend}
                        disabled={!message.trim() || disabled}
                        size="small"
                        sx={{
                          ...getSendButtonStyles(),
                          mr: 1,
                          transition: 'all 0.2s',
                        }}
                        aria-label={disabled ? t("Send message (disabled while waiting for response)") : t("Send message")}
                      >
                        <SendIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ),
              sx: {
                p: 1.5,
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                minHeight: '60px', // Set minimum height for the input
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  border: 'none',
                },
                '&:hover fieldset': {
                  border: 'none',
                },
                '&.Mui-focused fieldset': {
                  border: 'none',
                },
                // Add space for input content
                padding: '8px 14px',
              },
              '& .MuiInputBase-inputMultiline': {
                lineHeight: 1.5,
              },
            }}
          />
        </Paper>
        <Box 
          sx={{ 
            fontSize: "0.7rem", 
            color: theme.palette.text.secondary,
            textAlign: "center", 
            mt: 1.5,
            px: 2,
          }}
        >
          {t("Shoeb's portfolio assistant can provide information about projects, skills, and experience")}
        </Box>
      </Box>
    </Box>
  );
};

export default ChatInput;